import { ReactNode, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useOAuth } from "@/contexts/OAuthContext";
import { getValidatingSession } from "@/lib/queryClient";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const [location, navigate] = useLocation();
  const { isOAuthRedirect, oauthState } = useOAuth();
  const isValidatingSession = getValidatingSession();
  
  // Debug logging
  console.log("ProtectedRoute check:", {
    location,
    hasUser: !!user,
    userEmail: user?.email,
    isLoading,
    isValidatingSession,
    isOAuthProcessing: oauthState.isProcessing,
    isOAuthRedirect: isOAuthRedirect(),
    timestamp: new Date().toISOString()
  });
  
  // Redirect to login if not authenticated and not in OAuth flow
  useEffect(() => {
    const shouldRedirect = !user && !isLoading && !isValidatingSession && !isOAuthRedirect();
    
    if (shouldRedirect) {
      console.log("ProtectedRoute: Redirecting to login - no user and not in OAuth flow");
      navigate("/login");
    }
  }, [user, isLoading, navigate, isValidatingSession, isOAuthRedirect]);
  
  // Show loading state during OAuth processing
  if (isOAuthRedirect() || oauthState.isProcessing) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            Completing sign in...
          </p>
        </div>
      </div>
    );
  }
  
  // If not authenticated, return null (redirecting is handled by useEffect)
  if (!user) {
    console.log("ProtectedRoute: No user, returning null");
    return null;
  }
  
  // If authenticated, render children
  console.log("ProtectedRoute: User authenticated, rendering children");
  return <>{children}</>;
}
